import { ComponentType, forwardRef, ReactElement, useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import styled from 'styled-components';

import { TransitionDuration } from 'design-system/variables/transitions';

interface BaseNodePositionedProps {
  x: number;
  y: number;
  z: number;
  nodeId: string;
  renderNode: (baseNodeOptions: { isUnderDraggedItem: boolean }) => ReactElement;
  dragged?: boolean;
}

interface DragAndDropPayload {
  draggedNodeId: string;
  droppedNodeId: string | null;
}

interface DropResult {
  droppedInto: string;
}

const NodePositioned = styled.div.attrs<BaseNodePositionedProps>(
  ({ dragged = false, x, y, z }) => ({
    style: {
      transform: `translate(${String(x)}px, ${String(y)}px)`,
      zIndex: z,
      ...(dragged ? {} : { transition: `transform ${TransitionDuration.Fast}` }),
    },
  }),
)<BaseNodePositionedProps>`
  position: absolute;
  user-select: none;
`;

// Eventually we may wanna accept arbitrary key per node
const KEY_DND = 'flow-canvas__node';

const withDraggable = <
  T extends BaseNodePositionedProps & { onDraggingEnd: (payload: DragAndDropPayload) => void },
>(
  Component: ComponentType<Omit<T, 'onDraggingEnd' | 'renderNode'>>,
) => {
  const Wrapped = forwardRef<HTMLDivElement, T>(
    ({ onDraggingEnd, renderNode, ...props }, forwardedRef) => {
      const [, dragSourceRef, preview] = useDrag(
        () => ({
          type: KEY_DND,
          item: { id: props.nodeId },
          end: (item, monitor) =>
            onDraggingEnd({
              draggedNodeId: item.id,
              droppedNodeId: monitor.getDropResult<DropResult>()?.droppedInto ?? null,
            }),
        }),
        [props.nodeId, onDraggingEnd],
      );

      useEffect(() => {
        preview(getEmptyImage());
      }, [preview]);

      return (
        <Component
          {...props}
          ref={(el: HTMLDivElement) => {
            dragSourceRef(el);
            if (typeof forwardedRef === 'function') {
              forwardedRef(el);
            }
          }}
        >
          {renderNode({ isUnderDraggedItem: false })}
        </Component>
      );
    },
  );
  Wrapped.displayName = 'withDraggable';
  return Wrapped;
};

const withDroppable = <T extends BaseNodePositionedProps>(
  Component: ComponentType<Omit<T, 'renderNode'>>,
) => {
  const Wrapped = forwardRef<HTMLDivElement, T>(({ renderNode, ...props }, forwardedRef) => {
    const [{ isOver }, dropTargetRef] = useDrop<
      { id: string },
      DropResult,
      { isOver: boolean; isDragging: boolean }
    >(
      () => ({
        accept: KEY_DND,
        collect: (monitor) => ({
          isOver: monitor.isOver(),
          isDragging: !!monitor.getItem(),
        }),
        canDrop: (item) => item.id !== props.nodeId,
        drop: () => ({ droppedInto: props.nodeId }),
      }),
      [props.nodeId],
    );

    return (
      <Component
        {...props}
        ref={(el: HTMLDivElement) => {
          dropTargetRef(el);
          if (typeof forwardedRef === 'function') {
            forwardedRef(el);
          }
        }}
      >
        {renderNode({ isUnderDraggedItem: isOver })}
      </Component>
    );
  });
  Wrapped.displayName = 'withDroppable';
  return Wrapped;
};

const Droppable = withDroppable(NodePositioned);
const Draggable = withDraggable(NodePositioned);

const NodeWrapper = forwardRef<
  HTMLDivElement,
  {
    draggable?: boolean;
    droppable?: boolean;
    onDraggingEnd?: (payload: DragAndDropPayload) => void;
  } & BaseNodePositionedProps
>(
  (
    { nodeId, x, y, z, dragged, draggable = false, droppable = false, renderNode, onDraggingEnd },
    forwardedRef,
  ) => {
    const propsAndRef = {
      x,
      y,
      z,
      dragged,
      nodeId,
      renderNode,
      ref: (el: HTMLDivElement) => {
        if (typeof forwardedRef === 'function') {
          forwardedRef(el);
        }
      },
    };

    return draggable ? (
      <Draggable
        {...propsAndRef}
        onDraggingEnd={
          onDraggingEnd ??
          (() => {
            /* noop */
          })
        }
      />
    ) : droppable ? (
      <Droppable {...propsAndRef} />
    ) : (
      <NodePositioned {...propsAndRef}>{renderNode({ isUnderDraggedItem: false })}</NodePositioned>
    );
  },
);

NodeWrapper.displayName = 'NodeWrapper';
export default NodeWrapper;
